import {css, CSSResult, html, LitElement, nothing, TemplateResult, unsafeCSS,} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {consume} from '@lit/context';
import filterIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_filter.svg';
import {Messages} from '../../../@types/wishlist.js';
import styles from './filter-button.scss';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import {FilterMenu} from '../../../scripts/filter/filter-menu.js';
import {filterContext} from '../../../scripts/context/filterContext.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {repeat} from 'lit/directives/repeat.js';

// lazy load initially hidden components
import('./filter-overview.js');
import('./filter-group.js');

@customElement('ents-filter-button')
export class UIFilter extends LitElement {
  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: filterContext, subscribe: true})
  @property({attribute: false})
  private filterMenu!: FilterMenu;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`];
  }

  private setLayerOpen() {
    this.dispatchEvent(new Event('open'));
  }

  private activateFilterOption(optionId: string, allowRemove: boolean) {
    this.dispatchEvent(new CustomEvent('changed', {detail: {optionId, allowRemove}, bubbles: true, composed: true}));
    this.dispatchEvent(new Event('apply', {bubbles: true, composed: true}));
    this.dispatchEvent(new Event('scroll-top'));
  }

  private renderOneClickOptions() {
    return html`
      ${repeat(
        this.filterMenu.getOneClickOptions(),
        oneClickOption => `one-click-${oneClickOption.id}`,
        oneClickOption => html`
          <button
            variant="secondary"
            size="medium"
            class="bdk-chip"
            type="button"
            data-test-ents-filter-button-id="one-click-${oneClickOption.id}"
            @click="${() => this.activateFilterOption(oneClickOption.id, true)}"
            ?active="${oneClickOption.isActive(false)}"
          >
            <span class="ents-filter-button__button__label">
              ${unsafeHTML(oneClickOption.shortLabel(this.messages))}
            </span>
          </button>`,
      )}
    `;
  }

  protected render(): TemplateResult {
    const activeFilterCount = this.filterMenu.getAppliedActiveAndNotDefaultOptionsCount(true);

    return html`
      <div class="ents-filter-buttons">
        <div class="ents-filter-button" data-test-ents-filter-button-shadow>
          <button
            class="bdk-chip"
            variant="secondary"
            size="medium"
            type="button"
            @click="${this.setLayerOpen}"
            aria-label="${this.messages['ents.wishlist.filter.button.description']}"
            ?active="${activeFilterCount > 0}">
            ${hideIconFromAssistiveTechnology(filterIcon())}
            <span class="ents-filter-button__button__label ents-filter-button__button__label--all"
            >${this.messages['ents.wishlist.filter.button.all']}</span>
            ${activeFilterCount > 0 ? html`
              <div class="bdk-badge-count" size="small" data-test-ents-filter-button-count="${activeFilterCount}">
                ${activeFilterCount}
              </div>
            ` : nothing}
          </button>
        </div>
        ${this.renderOneClickOptions()}
      </div>
    `;
  }
}
